/**
 * @fileoverview Pattern Review step for the Edit Like Me wizard flow.
 * Presents the AI editing plan based on reference project patterns.
 * Users can enable/disable actions, view examples, add notes, and execute.
 */

import React, { useState, useCallback, useMemo, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import deepCutApi from '../services/deepcutApi';
import TrebbleCheckbox from '../../common/TrebbleCheckbox.tsx';
import TrebbleSelect from '../../common/TrebbleSelect.tsx';
import TrebbleInput from '../../common/TrebbleInput.tsx';
import useDarkModeStatus from '../../helper/useDarkModeStatus';
import useSequencerDarkModeStatus from '../../helper/useSequencerDarkModeStatus';
// @ts-ignore
import ti18n from 'i18n!nls/Deepcuti18n';
const { Option } = TrebbleSelect;

// ─── Constants ───────────────────────────────────────────────────────────────

const FALLBACK_POSITION_OPTIONS = [
  { value: 'beginning', i18nKey: 'POS_BEGINNING' },
  { value: 'end', i18nKey: 'POS_END' },
  { value: 'between_segments', i18nKey: 'POS_BETWEEN' },
  { value: 'before_guest', i18nKey: 'POS_BEFORE_GUEST' },
  { value: 'after_ad', i18nKey: 'POS_AFTER_AD' },
];


// ─── Plan-style action names for deletion patterns ──────────────────────────

// Maps pattern type keywords to i18n keys for plan action names
const PLAN_ACTION_I18N_KEYS = {
  filler: 'ACTION_CUT_FILLER_WORDS',
  disfluency: 'ACTION_REMOVE_REPETITIONS_FALSE_STARTS',
  redundancy: 'ACTION_REMOVE_REPETITIONS_FALSE_STARTS',
  repetiti: 'ACTION_REMOVE_REPETITIONS',
  false_start: 'ACTION_REMOVE_FALSE_STARTS',
  tangent: 'ACTION_TRIM_TANGENTS',
  off_topic: 'ACTION_CUT_OFF_TOPIC',
  long_pause: 'ACTION_SHORTEN_LONG_PAUSES',
  pause: 'ACTION_SHORTEN_PAUSES',
  verbal_crutch: 'ACTION_CLEAN_VERBAL_CRUTCHES',
  stammer: 'ACTION_CLEAN_STAMMERING',
  backstage: 'ACTION_REMOVE_META_COMMENTARY',
  meta_comment: 'ACTION_REMOVE_META_COMMENTARY',
  meta_commen: 'ACTION_REMOVE_META_COMMENTARY',
};

// ─── Theme-aware palettes ────────────────────────────────────────────────────
// CSS custom properties are set on the root container so every child
// resolves to theme-appropriate values.

const LIGHT_THEME_VARS = {
  '--text-default-color': '#1a1a1a',
  '--text-secondary-color': '#666',
  '--text-tertiary-color': '#999',
  '--bg-card': '#f9f8f6',
  '--bg-secondary': '#f9f8f6',
  '--border-default-color': '#e0ded8',
  '--pattern-removed-color': '#c44a4a',
  '--pattern-removed-bg': 'rgba(196, 74, 74, 0.07)',
  '--pattern-removed-border': 'rgba(196, 74, 74, 0.27)',
  '--pattern-kept-color': '#3a9e4a',
  '--pattern-kept-bg': 'rgba(58, 158, 74, 0.08)',
  '--pattern-kept-border': 'rgba(58, 158, 74, 0.27)',
  '--trebble-success-bg': 'rgba(58, 158, 74, 0.08)',
  '--trebble-success': '#3a9e4a',
  '--trebble-warning': '#b08a2e',
  '--trebble-warning-bg': 'rgba(176, 138, 46, 0.08)',
};

const DARK_THEME_VARS = {
  '--text-default-color': '#e8e6e1',
  '--text-secondary-color': '#aaa',
  '--text-tertiary-color': '#777',
  '--bg-card': '#2a2a2a',
  '--bg-secondary': '#242424',
  '--border-default-color': '#444',
  '--pattern-removed-color': '#e06060',
  '--pattern-removed-bg': 'rgba(224, 96, 96, 0.12)',
  '--pattern-removed-border': 'rgba(224, 96, 96, 0.3)',
  '--pattern-kept-color': '#5abe6a',
  '--pattern-kept-bg': 'rgba(90, 190, 106, 0.12)',
  '--pattern-kept-border': 'rgba(90, 190, 106, 0.3)',
  '--trebble-success-bg': 'rgba(90, 190, 106, 0.12)',
  '--trebble-success': '#5abe6a',
  '--trebble-warning': '#d4a843',
  '--trebble-warning-bg': 'rgba(212, 168, 67, 0.12)',
};

// Scoped CSS overrides for Ant Design internals that ignore inline styles.
// The dropdown is rendered inside .pattern-review-themed via getPopupContainer.
const getScopedStyles = (isDark) => `
  .pattern-review-themed .ant-select-selection-item {
    color: var(--trebble-primary, #d4654a) !important;
  }
  .pattern-review-themed .ant-select-dropdown .ant-select-item {
    color: ${isDark ? '#e8e6e1' : '#1a1a1a'} !important;
  }
  .pattern-review-themed .ant-select-dropdown .ant-select-item-option-selected {
    color: ${isDark ? '#e8e6e1' : '#1a1a1a'} !important;
    font-weight: 600;
  }
  .pattern-review-themed .ant-select-dropdown {
    background: ${isDark ? '#2a2a2a' : '#fff'} !important;
  }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function t(key) {
  return window.getI18n(ti18n, key) || key;
}

/**
 * Render an i18n template string with React element slots.
 * Template: "Add as {classification} at the {position}"
 * Slots: { classification: <InlineSelect />, position: <InlineSelect /> }
 * Returns an array of React elements suitable for JSX rendering.
 */
function renderWithSlots(template, slots) {
  const parts = template.split(/(\{[^}]+\})/);
  return parts.map(function(part, i) {
    var match = part.match(/^\{(.+)\}$/);
    if (match && slots[match[1]] !== undefined) {
      return React.createElement(React.Fragment, { key: i }, slots[match[1]]);
    }
    return part;
  });
}

/** Get a plan-style action name for a deletion pattern */
function getPlanActionName(patternType, displayLabel) {
  if (displayLabel) return displayLabel;
  if (!patternType) return t('ACTION_REMOVE_CONTENT');
  const normalized = patternType.toLowerCase().replace(/\s+/g, '_');
  // Check partial matches against known patterns
  for (const [key, i18nKey] of Object.entries(PLAN_ACTION_I18N_KEYS)) {
    if (normalized.includes(key)) return t(i18nKey);
  }
  // Fallback: use i18n template with the pattern type
  const cleaned = patternType.replace(/_/g, ' ').trim().toLowerCase();
  return t('ACTION_CUT_TEMPLATE').replace('{0}', cleaned);
}

/** Normalize frequency — accepts enum values directly or LLM free-text as fallback */
const FREQUENCY_ENUM = { always: 'always', often: 'often', sometimes: 'sometimes' };
function normalizeFrequency(freq) {
  if (!freq) return 'sometimes';
  const lower = freq.toLowerCase();
  // Accept enum values directly (language-independent)
  if (FREQUENCY_ENUM[lower]) return FREQUENCY_ENUM[lower];
  // Fallback: English free-text (for backward compat until Lambda sends enums)
  if (/very\s*high|nearly\s*all|always/i.test(lower)) return 'always';
  if (/high|often|most\s*cases|frequently/i.test(lower)) return 'often';
  return 'sometimes';
}

/** Normalize aggressiveness — accepts enum values directly or LLM free-text as fallback */
const AGGRESSIVENESS_ENUM = { aggressive: 'aggressive', moderate: 'moderate', light: 'light' };
function normalizeAggressiveness(val) {
  if (!val) return 'moderate';
  const lower = val.toLowerCase();
  // Accept enum values directly (language-independent)
  if (AGGRESSIVENESS_ENUM[lower]) return AGGRESSIVENESS_ENUM[lower];
  // Fallback: English free-text (for backward compat until Lambda sends enums)
  if (/aggressive|heavy|significant/i.test(lower)) return 'aggressive';
  if (/light|conservative|minimal/i.test(lower)) return 'light';
  return 'moderate';
}

/** Normalize position merging positionType and placement */
function normalizePosition(positionType, placement) {
  const raw = positionType || placement || 'beginning';
  const lower = raw.toLowerCase();
  if (/begin|start|intro/i.test(lower)) return 'beginning';
  if (/end|outro|closing/i.test(lower)) return 'end';
  if (/between|segment|mid/i.test(lower)) return 'between_segments';
  if (/before.*guest/i.test(lower)) return 'before_guest';
  if (/after.*ad/i.test(lower)) return 'after_ad';
  return raw;
}

/** Ensure a value is an array of strings */
function ensureStringArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean);
  return [];
}

/** Generate a unique ID */
let idCounter = 0;
function generateId(prefix) {
  idCounter += 1;
  return `${prefix}_${idCounter}`;
}

// ─── Data Initialization ─────────────────────────────────────────────────────

/** Example type constants — used in the Zod schema, never localized */
const EXAMPLE_TYPE = Object.freeze({ REMOVED: 'removed', KEPT: 'kept' });

/**
 * Enrich a plain-text example with surrounding context from reference snippets.
 * Searches the provided snippet pools for an occurrence of the example text and,
 * when found, constructs a taggedText with <cut>/<keep> markup so the UI
 * can display surrounding words for context.
 *
 * @param {Object} example - The example to enrich ({text: "um,"})
 * @param {string[][]} snippetPools - Arrays of snippets to search, in priority order
 * @param {string} type - EXAMPLE_TYPE.REMOVED or EXAMPLE_TYPE.KEPT
 */
function enrichExampleWithContext(example, snippetPools, type) {
  // Only enrich plain-text examples (no taggedText or contextBefore already set)
  if (example.taggedText || example.contextBefore != null) return example;
  const text = (example.text || '').trim().toLowerCase();
  if (!text) return example;

  // Search all snippet pools for a sentence containing this example text with surrounding words
  for (const snippets of snippetPools) {
    if (!snippets || snippets.length === 0) continue;
    for (const snippet of snippets) {
      // Only consider snippets that are longer than the example (have surrounding context)
      if (snippet.length <= text.length + 10) continue;
      const snippetLower = snippet.toLowerCase();
      // Use word-boundary matching to avoid false positives on short words like "um", "uh", "so"
      // but only apply boundaries when the example starts/ends with a word char — otherwise
      // examples with trailing punctuation like "um," or "uh..." would never match.
      const escapedText = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const leadingBoundary = /^\w/.test(text) ? '\\b' : '';
      const trailingBoundary = /\w$/.test(text) ? '\\b' : '';
      const wordBoundaryRegex = new RegExp(`${leadingBoundary}${escapedText}${trailingBoundary}`, 'i');
      const match = wordBoundaryRegex.exec(snippetLower);
      if (!match) continue;
      const idx = match.index;

      // Found — build taggedText with surrounding context
      const contextRadius = 60; // chars of context on each side
      const start = Math.max(0, idx - contextRadius);
      const end = Math.min(snippet.length, idx + text.length + contextRadius);
      const before = snippet.slice(start, idx);
      const matched = snippet.slice(idx, idx + text.length);
      const after = snippet.slice(idx + text.length, end);
      const tag = type === EXAMPLE_TYPE.KEPT ? 'keep' : 'cut';
      const taggedText =
        (start > 0 ? '...' : '') +
        before +
        `<${tag}>` + matched + `</${tag}>` +
        after +
        (end < snippet.length ? '...' : '');

      return { ...example, taggedText };
    }
  }

  return example;
}

function buildInitialState(input) {
  // Pre-extract reference snippets for context enrichment
  const refExamplesRaw = input.referenceExamples || {};
  const keptSnippetsForContext = ensureStringArray(refExamplesRaw.keptSegments || refExamplesRaw.keptSnippets);
  const removedSnippetsForContext = ensureStringArray(refExamplesRaw.removedSegments || refExamplesRaw.removedSnippets);

  const deletionPatterns = (input.deletionPatterns || []).map((p, i) => {
    // Normalize and split examples into two arrays at data level
    const rawExamples = Array.isArray(p.examples) ? p.examples : [];
    const removedExamples = [];
    const keptExamples = [];

    for (const ex of rawExamples) {
      const normalized = typeof ex === 'string' ? { text: ex } : ex;
      if (normalized.type === EXAMPLE_TYPE.KEPT) {
        // Enrich with context: search keptSnippets first, then removedSnippets
        keptExamples.push(enrichExampleWithContext(normalized, [keptSnippetsForContext, removedSnippetsForContext], EXAMPLE_TYPE.KEPT));
      } else {
        // Enrich removed examples: search removedSnippets first (filler words may appear in longer removed passages), then keptSnippets
        removedExamples.push(enrichExampleWithContext(normalized, [removedSnippetsForContext, keptSnippetsForContext], EXAMPLE_TYPE.REMOVED));
      }
    }

    return {
      _id: generateId('del'),
      patternType: p.patternType || `pattern_${i}`,
      shortLabel: p.shortLabel || '',
      description: p.description || '',
      humanSummary: p.pipelineGuidance || p.description || '',
      frequency: p.frequency || 'sometimes',
      pipelineGuidance: p.pipelineGuidance || '',
      examples: rawExamples.map(ex => typeof ex === 'string' ? { text: ex } : ex),
      enabled: true,
      note: '',
      removedExamples,
      keptExamples,
      remPage: 0,
      keptPage: 0,
    };
  });

  const refExamples = input.referenceExamples || {};
  const removedSegments = ensureStringArray(refExamples.removedSegments || refExamples.removedSnippets);
  const keptSegments = ensureStringArray(refExamples.keptSegments || refExamples.keptSnippets);

  const removedExamplesGlobal = removedSegments.map((text) => ({
    _id: generateId('rem'),
    text,
    enabled: true,
  }));

  const keptExamplesGlobal = keptSegments.map((text) => ({
    _id: generateId('kept'),
    text,
    enabled: true,
  }));

  const structural = input.structuralPatterns || {};
  const rawAgg = structural.aggressivenessLevel || structural.overallAggressiveness || 'moderate';
  const aggressiveness = normalizeAggressiveness(rawAgg);
  const rawDeletionPct = structural.deletionPercentage;
  const deletionPercentage = rawDeletionPct != null
    ? Number(String(rawDeletionPct).replace(/[^0-9.]/g, '')) || null
    : null;

  const additionPatterns = (input.additionPatterns || []).map((a, i) => {
    // Get description options from Lambda or audio classification
    const descOptions = a.descriptionOptions
      || (a.audioClassification?.description ? [a.audioClassification.description] : []);

    // V2 fields: category, semanticPosition, examples, classification
    const category = a.category || (a.type === 'background_music' ? 'wrap' : 'insert');
    const semanticPosition = a.semanticPosition || null;
    const fewShotExamples = (a.examples || []).map(ex => ex.contextWithTag || (typeof ex === 'string' ? ex : ''));
    const geminiClassification = a.classification || a.audioClassification?.description || null;

    // Use userSelectedType (from cache override) when available, otherwise fall back to Lambda type
    const initialClassification = a.userSelectedType || a.type || 'sound_effect';

    return {
      _id: generateId('add'),
      // type serves as the "original" baseline for additionsModified comparison.
      // Use initialClassification so that a previously saved user selection is not
      // counted as a fresh in-session edit on the very first render.
      type: initialClassification,
      fileTitle: a.fileInfo?.title || a.audioFileInfo?.title || `Audio ${i + 1}`,
      fileInfo: a.fileInfo || a.audioFileInfo || null,
      description: a.description || a.rationale || '',
      classification: initialClassification,
      customClassification: '',
      position: normalizePosition(a.positionType, a.placement),
      customPosition: '',
      enabled: true,
      note: '',
      userDesc: '',
      descriptionOptions: descOptions,
      selectedDescription: descOptions[0] || '',
      customDescription: '',
      // V2 fields
      category,
      semanticPosition,
      fewShotExamples: fewShotExamples.filter(Boolean),
      geminiClassification,
      frequency: a.frequency || null,
      typeLabel: a.typeLabel || null,
      positionLabel: a.positionLabel || null,
    };
  });

  return {
    deletionPatterns,
    additionPatterns,
    aggressiveness,
    deletionPercentage,
    removedExamples: removedExamplesGlobal,
    keptExamples: keptExamplesGlobal,
    structural,
    styleInsights: input.styleInsights || {},
    patternsS3Url: input.patternsS3Url || null,
  };
}

// ─── Sub-Components ──────────────────────────────────────────────────────────

/**
 * Parse tagged text with <cut>...</cut> or <keep>...</keep> inline markup.
 * Returns an array of { text, tag } segments for rendering.
 */
function parseTaggedText(taggedText) {
  if (!taggedText) return [];
  const regex = /<(cut|keep)>([\s\S]*?)<\/\1>/gi;
  const segments = [];
  let lastIndex = 0;

  let match;
  while ((match = regex.exec(taggedText)) !== null) {
    // Text before the tag
    if (match.index > lastIndex) {
      segments.push({ text: taggedText.slice(lastIndex, match.index), tag: null });
    }
    // Tagged text
    segments.push({ text: match[2], tag: match[1].toLowerCase() });
    lastIndex = regex.lastIndex;
  }
  // Text after the last tag
  if (lastIndex < taggedText.length) {
    segments.push({ text: taggedText.slice(lastIndex), tag: null });
  }
  return segments;
}

/** Max characters for an example display — prevents a single example from filling the screen */
const MAX_EXAMPLE_CHARS = 200;

/** Truncate text to a max length, preserving word boundaries */
function truncateText(text, max) {
  if (!text || text.length <= max) return text;
  const truncated = text.slice(0, max);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? truncated.slice(0, lastSpace) : truncated) + '...';
}

/** Render a contextual example — supports tagged format, contextual format, and legacy */
function ContextualExample({ example, type, highlightStyle }) {
  // Tagged format: taggedText with <cut>/<keep> inline markup
  if (example.taggedText) {
    const taggedText = example.taggedText.length > MAX_EXAMPLE_CHARS
      ? truncateText(example.taggedText.replace(/<\/?(?:cut|keep)>/gi, ''), MAX_EXAMPLE_CHARS)
      : example.taggedText;
    const segments = parseTaggedText(taggedText);
    return (
      <span>
        {segments.map((seg, i) => (
          seg.tag
            ? <span key={i} style={highlightStyle}>{seg.text}</span>
            : <span key={i}>{seg.text}</span>
        ))}
      </span>
    );
  }
  // Contextual format: contextBefore + highlighted + contextAfter (legacy v1)
  if (example.contextBefore != null && example.highlighted) {
    return (
      <span>
        {example.contextBefore ? <>{example.contextBefore} </> : null}
        <span style={highlightStyle}>{example.highlighted}</span>
        {example.contextAfter ? <> {example.contextAfter}</> : null}
      </span>
    );
  }
  // Plain text fallback
  const rawText = typeof example === 'string' ? example : (example.text || example.before || '');
  return <span style={highlightStyle}>{truncateText(rawText, MAX_EXAMPLE_CHARS)}</span>;
}

/** Single-type example carousel with pager */
function SingleExampleCarousel({ label, labelColor, bgColor, borderColor, items, type }) {
  const [page, setPage] = useState(0);
  if (!items || items.length === 0) return null;

  const current = items[page] || items[0];
  const total = items.length;

  const highlightStyle = type === 'removed'
    ? { textDecoration: 'line-through', textDecorationColor: 'var(--pattern-removed-color)', color: 'var(--pattern-removed-color)', textDecorationThickness: '1.5px' }
    : { fontWeight: 600, color: 'var(--pattern-kept-color)' };

  return (
    <div className="mb-2">
      <div className="flex items-center justify-between mb-1">
        <span className="uppercase tracking-wider font-semibold" style={{ color: labelColor, fontSize: 'var(--text-xs, 12px)' }}>
          {label}
        </span>
        {total > 1 && (
          <div className="flex items-center gap-1">
            <button
              className="w-[20px] h-[20px] flex items-center justify-center border-0 bg-transparent cursor-pointer"
              style={{ color: 'var(--text-secondary-color)', opacity: page <= 0 ? 0.25 : 1, fontSize: 'var(--text-base, 16px)' }}
              onClick={() => page > 0 && setPage(page - 1)}
              disabled={page <= 0}
            >&#8249;</button>
            <span className="font-mono" style={{ color: 'var(--text-tertiary-color)', fontSize: 'var(--text-xs, 12px)' }}>
              {page + 1}/{total}
            </span>
            <button
              className="w-[20px] h-[20px] flex items-center justify-center border-0 bg-transparent cursor-pointer"
              style={{ color: 'var(--text-secondary-color)', opacity: page >= total - 1 ? 0.25 : 1, fontSize: 'var(--text-base, 16px)' }}
              onClick={() => page < total - 1 && setPage(page + 1)}
              disabled={page >= total - 1}
            >&#8250;</button>
          </div>
        )}
      </div>
      <div
        className="p-2 leading-relaxed"
        style={{
          fontSize: 'var(--text-base, 16px)',
          background: bgColor,
          borderLeft: `2px solid ${borderColor}`,
          color: 'var(--text-default-color)',
        }}
      >
        <ContextualExample example={current} type={type} highlightStyle={highlightStyle} />
      </div>
    </div>
  );
}

/** Two carousels: "We'll cut segments like this" and "We'll keep segments like this" */
function ExampleCarousels({ removedExamples, keptExamples }) {
  // Examples are pre-split in buildInitialState — no filtering needed here
  if ((!removedExamples || removedExamples.length === 0) && (!keptExamples || keptExamples.length === 0)) return null;

  return (
    <>
      <SingleExampleCarousel
        label={t('EXAMPLE_CUT_LABEL')}
        labelColor="var(--pattern-removed-color, #c44a4a)"
        bgColor="var(--pattern-removed-bg, rgba(196, 74, 74, 0.07))"
        borderColor="var(--pattern-removed-border, rgba(196, 74, 74, 0.27))"
        items={removedExamples || []}
        type={EXAMPLE_TYPE.REMOVED}
      />
      <SingleExampleCarousel
        label={t('EXAMPLE_KEEP_LABEL')}
        labelColor="var(--pattern-kept-color, #3a9e4a)"
        bgColor="var(--pattern-kept-bg, rgba(58, 158, 74, 0.08))"
        borderColor="var(--pattern-kept-border, rgba(58, 158, 74, 0.27))"
        items={keptExamples || []}
        type={EXAMPLE_TYPE.KEPT}
      />
    </>
  );
}

/** Always-visible note input */
function NoteInput({ note, onNoteChange, placeholder }) {
  return (
    <TrebbleInput
      value={note || ''}
      onChange={(e) => onNoteChange(e.target.value)}
      placeholder={placeholder || t('ADD_NOTE_PLACEHOLDER')}
      useEditorTheme={false}
      className="mt-1 w-full"
      style={{
        fontSize: 'var(--text-base, 16px)',
        borderColor: 'var(--border-default-color)',
        fontStyle: note ? 'normal' : 'italic',
      }}
    />
  );
}

/**
 * Merge per-pattern examples with global reference examples.
 * When per-pattern examples are plain text (no taggedText/context), the global
 * reference examples provide additional contextual sentences so the user can
 * see the edits in context.
 */
function mergeExamplesWithGlobal(patternExamples, globalExamples) {
  if (!patternExamples || patternExamples.length === 0) return globalExamples || [];
  if (!globalExamples || globalExamples.length === 0) return patternExamples;

  // Check if any per-pattern examples already have context (taggedText or contextBefore)
  const hasContextual = patternExamples.some(ex => ex.taggedText || ex.contextBefore != null);
  if (hasContextual) return patternExamples;

  // Per-pattern examples are all plain text — prepend global examples that have context
  const contextualGlobals = globalExamples.filter(ex =>
    (ex.text || '').length > 20 || ex.taggedText || ex.contextBefore != null
  );
  if (contextualGlobals.length === 0) return patternExamples;

  // Put contextual global examples first, then the pattern-specific ones
  return [...contextualGlobals, ...patternExamples];
}

// ─── Deletion Pattern Item ───────────────────────────────────────────────────

function DeletionPatternItem({
  pattern,
  isExpanded,
  onToggleExpand,
  onToggleEnabled,
  onNoteChange,
  removedExamples,
  keptExamples,
}) {
  // Short label for the card header (2-4 words), long description for detail
  const actionName = pattern.shortLabel || getPlanActionName(pattern.patternType, pattern.typeLabel);
  // Show humanSummary as a compact subtitle — truncate to ~120 chars
  const subtitle = pattern.humanSummary && pattern.humanSummary !== actionName
    ? (pattern.humanSummary.length > 120 ? pattern.humanSummary.slice(0, 120) + '...' : pattern.humanSummary)
    : null;

  return (
    <div
      className="mb-[3px] border transition-opacity"
      style={{
        borderColor: 'var(--border-default-color)',
        background: 'var(--bg-card)',
        opacity: pattern.enabled ? 1 : 0.4,
      }}
    >
      {/* Collapsed row */}
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none"
        onClick={() => onToggleExpand(pattern._id)}
      >
        <TrebbleCheckbox
          checked={pattern.enabled}
          onChange={() => onToggleEnabled(pattern._id)}
          onClick={(e) => e.stopPropagation()}
          useEditorTheme={false}
        />
        <div className="flex-1 min-w-0">
          <div
            className="font-semibold"
            style={{
              fontSize: 'var(--text-base, 16px)',
              color: 'var(--text-default-color)',
              textDecoration: pattern.enabled ? 'none' : 'line-through',
              textDecorationColor: 'var(--text-tertiary-color, #aaa)',
            }}
          >
            {actionName}
          </div>
          {/* Compact subtitle from humanSummary */}
          {subtitle && (
            <div style={{ fontSize: 'var(--text-xs, 12px)', color: 'var(--text-secondary-color)', marginTop: 2, lineHeight: 1.3 }}>
              {subtitle}
            </div>
          )}
          {/* ELM v2: Show synthesis reasoning when available */}
          {pattern.synthesisReasoning && !subtitle && (
            <div style={{ fontSize: 'var(--text-xs, 12px)', color: 'var(--text-secondary-color)', marginTop: 2, lineHeight: 1.3 }}>
              {pattern.synthesisReasoning}
            </div>
          )}
        </div>
        <span
          className="flex-shrink-0 w-[14px] text-center transition-transform"
          style={{
            fontSize: 'var(--text-xs, 12px)',
            color: 'var(--text-tertiary-color)',
            transform: isExpanded ? 'rotate(90deg)' : 'none',
          }}
        >&#9656;</span>
      </div>

      {/* Expanded detail */}
      <div style={{ maxHeight: isExpanded ? 600 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
        <div className="px-3 py-2" style={{ borderTop: '1px solid var(--border-default-color, #e0ded8)' }}>
          <ExampleCarousels
            removedExamples={mergeExamplesWithGlobal(pattern.removedExamples, removedExamples)}
            keptExamples={mergeExamplesWithGlobal(pattern.keptExamples, keptExamples)}
          />
          <NoteInput
            note={pattern.note}
            onNoteChange={(val) => onNoteChange(pattern._id, val)}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Inline dropdown (styled like dashed underline) ─────────────────────────

function InlineSelect({ value, options, customValue, onChange, onCustomChange, placeholder }) {
  const isCustom = value === 'custom';

  if (isCustom) {
    return (
      <TrebbleInput
        value={customValue || ''}
        onChange={(e) => onCustomChange(e.target.value)}
        placeholder={placeholder || t('ADDITION_TYPE_HERE')}
        size="small"
        useEditorTheme={false}
        className="inline-block"
        style={{
          fontSize: 'inherit',
          width: 'auto',
          minWidth: 120,
          maxWidth: 200,
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderBottom: '1.5px dashed var(--trebble-primary, #d4654a)',
          background: 'transparent',
          color: 'var(--trebble-primary, #d4654a)',
          padding: '0 2px 1px',
        }}
      />
    );
  }

  return (
    <TrebbleSelect
      value={value}
      onChange={onChange}
      size="small"
      variant="borderless"
      useEditorTheme={false}
      className="inline-flex"
      style={{
        fontSize: 'inherit',
        width: 'auto',
        maxWidth: '100%',
        minWidth: 80,
        color: 'var(--trebble-primary, #d4654a)',
      }}
      popupMatchSelectWidth={false}
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
      dropdownStyle={{
        fontSize: 'var(--text-base, 16px)',
        color: 'var(--text-default-color)',
        background: '#fff',
        maxWidth: 500,
      }}
    >
      {options.map(opt => (
        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
      ))}
    </TrebbleSelect>
  );
}

// ─── Addition Pattern Item (collapsible, matching deletion pattern) ──────────

function AdditionPatternItem({
  addition,
  isExpanded,
  onToggleExpand,
  onToggleEnabled,
  onNoteChange,
  onPositionChange,
  onCustomPositionChange,
}) {
  // Build position dropdown options:
  // - If LLM positionOptions exist (from addition detector), use them
  // - Otherwise, use the semanticPosition as the default + Custom
  // - Never show generic hardcoded fallbacks — they're not relevant to the user's content
  const posOptions = useMemo(() => {
    const customOption = { value: 'custom', label: t('POS_CUSTOM') || 'Custom...' };
    if (addition.positionOptions && addition.positionOptions.length > 0) {
      const withoutCustom = addition.positionOptions.filter(o => o.value !== 'custom');
      return [...withoutCustom, customOption];
    }
    // Use semantic position as the sole meaningful option + Custom for override
    const semantic = addition.semanticPosition || addition.positionLabel || '';
    if (semantic) {
      return [
        { value: addition.position || 'semantic', label: semantic },
        customOption,
      ];
    }
    // Last resort: generic fallbacks
    return [
      ...FALLBACK_POSITION_OPTIONS.map(opt => ({ value: opt.value, label: t(opt.i18nKey) })),
      customOption,
    ];
  }, [addition.positionOptions, addition.semanticPosition, addition.positionLabel, addition.position]);

  // Collapsed subtitle: show custom override if set, else selected position, else semantic default
  const placementSubtitle = (addition.position === 'custom' && addition.customPosition)
    ? addition.customPosition
    : posOptions.find(o => o.value === addition.position)?.label
    || addition.semanticPosition
    || addition.positionLabel
    || addition.position
    || '';

  // Description for expanded view (informational, not editable)
  const audioDescription = addition.selectedDescription
    || addition.geminiClassification
    || addition.description
    || '';

  // Audio preview URL
  const audioUrl = addition.fileInfo?.audioUrl || null;

  return (
    <div
      className="mb-[3px] border transition-opacity"
      style={{
        borderColor: 'var(--border-default-color)',
        background: 'var(--bg-card)',
        opacity: addition.enabled ? 1 : 0.4,
      }}
    >
      {/* Collapsed row */}
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none"
        onClick={() => onToggleExpand(addition._id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpand(addition._id);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
      >
        <TrebbleCheckbox
          checked={addition.enabled}
          onChange={() => onToggleEnabled(addition._id)}
          onClick={(e) => e.stopPropagation()}
          useEditorTheme={false}
        />
        <div className="flex-1 min-w-0">
          <div
            className="font-semibold"
            style={{
              fontSize: 'var(--text-base, 16px)',
              color: 'var(--text-default-color)',
              textDecoration: addition.enabled ? 'none' : 'line-through',
              textDecorationColor: 'var(--text-tertiary-color, #aaa)',
            }}
          >
            {addition.fileTitle}
          </div>
          {placementSubtitle && (
            <div style={{ fontSize: 'var(--text-xs, 12px)', color: 'var(--text-secondary-color)', marginTop: 2, lineHeight: 1.3 }}>
              {placementSubtitle}
            </div>
          )}
        </div>
        <span
          className="flex-shrink-0 w-[14px] text-center transition-transform"
          style={{
            fontSize: 'var(--text-xs, 12px)',
            color: 'var(--text-tertiary-color)',
            transform: isExpanded ? 'rotate(90deg)' : 'none',
          }}
        >&#9656;</span>
      </div>

      {/* Expanded detail */}
      <div style={{ maxHeight: isExpanded ? 600 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
        <div className="px-3 py-2" style={{ borderTop: '1px solid var(--border-default-color, #e0ded8)' }}>
          <div style={{ marginLeft: 26 }}>
            {/* Play button + audio description (informational) */}
            {(audioUrl || audioDescription) && (
              <div className="flex items-center gap-2 mb-3" style={{ fontSize: 'var(--text-base, 16px)' }}>
                {audioUrl && (
                  <AudioPreviewButton audioUrl={audioUrl} isExpanded={isExpanded} />
                )}
                {audioDescription && (
                  <span style={{ color: 'var(--text-secondary-color)', lineHeight: 1.4 }}>
                    {audioDescription}
                  </span>
                )}
              </div>
            )}

            {/* "Place it [dropdown]" — the key adjustable */}
            <div style={{ fontSize: 'var(--text-base, 16px)', color: 'var(--text-default-color)', lineHeight: 1.7, marginBottom: 10 }}>
              {renderWithSlots(t('ADDITION_PLACE_IT_TEMPLATE') || 'Place it {position}', {
                position: <InlineSelect
                  value={addition.position}
                  options={posOptions}
                  customValue={addition.customPosition}
                  onChange={(val) => onPositionChange(addition._id, val)}
                  onCustomChange={(val) => onCustomPositionChange(addition._id, val)}
                  placeholder={t('DESCRIBE_POSITION')}
                />
              })}
            </div>

            {/* Note — always visible */}
            <NoteInput
              note={addition.note}
              onNoteChange={(val) => onNoteChange(addition._id, val)}
              placeholder={t('ADDITION_NOTE_PLACEHOLDER') || t('ADD_NOTE_PLACEHOLDER')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Audio Preview Button ──────────────────────────────────────────────────

function AudioPreviewButton({ audioUrl, isExpanded }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const previewLabel = isPlaying
    ? t('ADDITION_AUDIO_PREVIEW_PAUSE')
    : t('ADDITION_AUDIO_PREVIEW_PLAY');

  const togglePlay = useCallback((e) => {
    e.stopPropagation();
    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.addEventListener('ended', () => setIsPlaying(false));
      audioRef.current.addEventListener('pause', () => setIsPlaying(false));
    }
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => setIsPlaying(false));
      setIsPlaying(true);
    }
  }, [audioUrl, isPlaying]);

  // Stop playing when section collapses
  useEffect(() => {
    if (!isExpanded && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [isExpanded]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <button
      className="flex items-center justify-center flex-shrink-0 border cursor-pointer"
      style={{
        width: 28,
        height: 28,
        background: 'transparent',
        borderColor: 'var(--border-default-color)',
        color: isPlaying ? 'var(--trebble-primary, #d4654a)' : 'var(--text-secondary-color)',
        fontSize: 12,
        fontFamily: 'inherit',
      }}
      onClick={togglePlay}
      title={previewLabel}
      aria-label={previewLabel}
    >
      {isPlaying ? '\u275A\u275A' : '\u25B6'}
    </button>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

const PatternReviewStep = forwardRef(function PatternReviewStep({
  referenceEditingPatterns,
  synthesisResult,
  onApprove,
  onBack,
}, ref) {
  // Detect dark mode from app + system preferences
  const appDarkMode = useDarkModeStatus();
  const editorDarkMode = useSequencerDarkModeStatus();
  const systemDarkMode = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDarkMode = appDarkMode || editorDarkMode || systemDarkMode;
  const themeVars = isDarkMode ? DARK_THEME_VARS : LIGHT_THEME_VARS;

  const originalInput = useRef(referenceEditingPatterns);
  const [state, setState] = useState(() => {
    const initial = buildInitialState(referenceEditingPatterns || {});

    // ELM v2: enrich deletion patterns with synthesis reasoning and status
    if (synthesisResult?.plan && initial.deletionPatterns) {
      for (const planItem of synthesisResult.plan) {
        const match = initial.deletionPatterns.find(p =>
          p.patternType === planItem.referencePatternType || p.patternType === planItem.chipLabel
        );
        if (match) {
          match.synthesisReasoning = planItem.reasoning;
          match.synthesisStatus = planItem.status;
          match.synthesisConfidence = planItem.confidence;
          match.styleChoiceNote = planItem.styleChoiceNote;
        }
      }
    }

    return initial;
  });
  const [expandedItems, setExpandedItems] = useState(new Set());

  // --- Audio classification for additions (on mount) ---
  // Calls Gemini to classify each audio file, then enriches descriptionOptions
  // so the Plan shows "IBB Intro 3.mp3 is [upbeat intro jingle]" etc.
  // classMapRef persists results across resets so they can be re-applied.
  const classMapRef = useRef({});

  // Debounce timers keyed by audioUrl — one timer per audio file so changing
  // different items within 800ms doesn't cancel each other's saves.
  const saveUserSelectionTimerRef = useRef({});

  const applyClassificationsToPatterns = useCallback((patterns, classMap) => {
    return patterns.map(a => {
      const classification = classMap[a.fileInfo?.audioUrl];
      if (!classification) return a;
      const geminiDesc = classification.description;
      if (!geminiDesc) return a;
      // Merge Gemini description into existing options (prepend if not already present)
      // rather than replacing, so any user-selected option stays in the dropdown.
      const existingOpts = a.descriptionOptions || [];
      const mergedOpts = existingOpts.includes(geminiDesc)
        ? existingOpts
        : [geminiDesc, ...existingOpts];
      // Only update selectedDescription if the user hasn't changed it from the initial default.
      const userHasEdited = existingOpts.length > 0
        && a.selectedDescription !== (existingOpts[0] || '');
      // Extract LLM-generated typeOptions from Gemini classification result.
      // Only set if the addition doesn't already have typeOptions (don't overwrite user-provided).
      const geminiTypeOptions = classification.typeOptions;
      return {
        ...a,
        descriptionOptions: mergedOpts,
        selectedDescription: userHasEdited ? a.selectedDescription : geminiDesc,
        geminiClassification: geminiDesc || a.geminiClassification,
        typeOptions: (a.typeOptions && a.typeOptions.length > 0) ? a.typeOptions : geminiTypeOptions || a.typeOptions,
      };
    });
  }, []);

  useEffect(() => {
    const additions = state.additionPatterns;
    if (!additions || additions.length === 0) return;

    // Only classify if at least one addition lacks descriptionOptions
    const needsClassification = additions.some(a => !a.descriptionOptions || a.descriptionOptions.length === 0);
    if (!needsClassification) return;

    // Build the pattern array expected by classifyAdditionPatterns
    const patternsForClassification = additions
      .filter(a => a.fileInfo?.audioUrl)
      .map(a => ({ fileInfo: a.fileInfo }));
    if (patternsForClassification.length === 0) return;

    console.log('[ELM-CLASSIFY] Classifying', patternsForClassification.length, 'additions:', patternsForClassification.map(p => p.fileInfo?.audioUrl));
    let cancelled = false;
    deepCutApi.classifyAdditionPatterns(patternsForClassification).then((results) => {
      console.log('[ELM-CLASSIFY] Classification results:', results?.length || 0, results);
      if (cancelled || !results || results.length === 0) return;

      // Build a map of audioUrl -> inner classification object
      // Backend wraps: { classification: { type, description, ... }, classificationMethod }
      const classMap = {};
      for (const result of results) {
        if (result.success && result.classification) {
          const inner = result.classification.classification || result.classification;
          classMap[result.audioUrl] = inner;
        }
      }
      if (Object.keys(classMap).length === 0) return;

      // Persist so reset can re-apply them
      classMapRef.current = classMap;

      setState(prev => ({
        ...prev,
        additionPatterns: applyClassificationsToPatterns(prev.additionPatterns, classMap),
      }));
    });

    return () => { cancelled = true; };
  }, []); // Run once on mount

  // ELM v2: "Style maps cleanly" banner when synthesis had 0 questions
  const showCleanMappingBanner = synthesisResult && (!synthesisResult.styleChoices || synthesisResult.styleChoices.length === 0);

  // ─── State update helpers ────────────────────────────────────────────────

  const updateDeletionPattern = useCallback((id, updater) => {
    setState(prev => ({
      ...prev,
      deletionPatterns: prev.deletionPatterns.map(p =>
        p._id === id ? { ...p, ...updater(p) } : p
      ),
    }));
  }, []);

  const updateAdditionPattern = useCallback((id, updater) => {
    setState(prev => ({
      ...prev,
      additionPatterns: prev.additionPatterns.map(a =>
        a._id === id ? { ...a, ...updater(a) } : a
      ),
    }));
  }, []);

  // ─── Expand/collapse ────────────────────────────────────────────────────

  const toggleExpand = useCallback((id) => {
    setExpandedItems(prev => {
      // Auto-collapse: only one item open at a time
      if (prev.has(id)) return new Set(); // close if already open
      return new Set([id]); // open this one, close all others
    });
  }, []);

  // ─── Deletion pattern handlers ──────────────────────────────────────────

  const toggleDeletionEnabled = useCallback((id) => {
    updateDeletionPattern(id, p => ({ enabled: !p.enabled }));
  }, [updateDeletionPattern]);

  const setDeletionNote = useCallback((id, note) => {
    updateDeletionPattern(id, () => ({ note }));
  }, [updateDeletionPattern]);

  // ─── Addition pattern handlers ──────────────────────────────────────────

  const toggleAdditionEnabled = useCallback((id) => {
    updateAdditionPattern(id, a => ({ enabled: !a.enabled }));
  }, [updateAdditionPattern]);

  const setAdditionNote = useCallback((id, note) => {
    updateAdditionPattern(id, () => ({ note }));
  }, [updateAdditionPattern]);

  const setPosition = useCallback((id, position) => {
    updateAdditionPattern(id, (prev) => ({
      position,
      customPosition: position !== 'custom' ? '' : prev.customPosition,
    }));
  }, [updateAdditionPattern]);

  const setCustomPosition = useCallback((id, val) => {
    updateAdditionPattern(id, () => ({ customPosition: val }));
  }, [updateAdditionPattern]);


  // ─── Example paging (global removed/kept) ───────────────────────────────

  const [removedPage, setRemovedPage] = useState(0);
  const [keptPage, setKeptPage] = useState(0);

  const toggleRemovedExample = useCallback((exId) => {
    setState(prev => ({
      ...prev,
      removedExamples: prev.removedExamples.map(e =>
        e._id === exId ? { ...e, enabled: !e.enabled } : e
      ),
    }));
  }, []);

  const toggleKeptExample = useCallback((exId) => {
    setState(prev => ({
      ...prev,
      keptExamples: prev.keptExamples.map(e =>
        e._id === exId ? { ...e, enabled: !e.enabled } : e
      ),
    }));
  }, []);

  // ─── Reset ──────────────────────────────────────────────────────────────

  const handleReset = useCallback(() => {
    // Cancel any pending debounced saves so a Reset doesn't persist discarded values
    Object.values(saveUserSelectionTimerRef.current).forEach(clearTimeout);
    saveUserSelectionTimerRef.current = {};

    idCounter = 0;
    const initial = buildInitialState(originalInput.current || {});
    // Re-apply Gemini classifications that were fetched on mount (they survive in classMapRef)
    if (Object.keys(classMapRef.current).length > 0 && initial.additionPatterns) {
      initial.additionPatterns = applyClassificationsToPatterns(initial.additionPatterns, classMapRef.current);
    }
    setState(initial);
    setExpandedItems(new Set());
    setRemovedPage(0);
    setKeptPage(0);
  }, [applyClassificationsToPatterns]);

  // ─── Approve ────────────────────────────────────────────────────────────

  const handleApprove = useCallback(() => {
    const input = originalInput.current || {};
    const origStructural = input.structuralPatterns || {};
    const origStyleInsights = input.styleInsights || {};

    const enabledDeletions = state.deletionPatterns.filter(p => p.enabled);
    const enabledAdditions = state.additionPatterns.filter(a => a.enabled);

    const deletionPatterns = enabledDeletions.map(p => {
      let guidance = p.pipelineGuidance || p.description;
      if (p.note && p.note.trim()) {
        guidance += `\n[USER INSTRUCTION: ${p.note.trim()}]`;
      }
      return {
        patternType: p.patternType,
        description: p.description,
        frequency: normalizeFrequency(p.frequency),
        pipelineGuidance: guidance,
        examples: p.examples,
      };
    });

    const additionPatterns = enabledAdditions.map(a => {
      const classValue = a.classification === 'custom' ? a.customClassification : a.classification;
      const posValue = a.position === 'custom' ? a.customPosition : a.position;
      const descValue = a.selectedDescription === 'custom' ? a.customDescription : (a.selectedDescription || '');
      let rationale = a.userDesc || a.description;
      if (descValue) {
        rationale = `${descValue}. ${rationale}`;
      }
      if (a.note && a.note.trim()) {
        rationale += `\n[USER INSTRUCTION: ${a.note.trim()}]`;
      }
      return {
        type: classValue || a.type,
        category: a.category || 'insert',
        positionType: posValue,
        semanticPosition: a.semanticPosition || null,
        fileInfo: a.fileInfo || null,
        rationale,
        audioDescription: descValue,
        examples: a.fewShotExamples ? a.fewShotExamples.map(ex => ({ contextWithTag: ex })) : undefined,
      };
    });

    const preservedRaw = origStructural.preservedContentTypes || origStructural.consistentlyPreservedContent;
    const removedRaw = origStructural.removedContentTypes || origStructural.consistentlyRemovedContent;

    const structuralPatterns = {
      aggressivenessLevel: state.aggressiveness,
      deletionPercentage: origStructural.deletionPercentage || null,
      preservedContentTypes: ensureStringArray(preservedRaw),
      removedContentTypes: ensureStringArray(removedRaw),
      strategySummary: origStructural.strategySummary || '',
    };

    const contentPrioritiesRaw = origStyleInsights.contentPriorities || origStyleInsights.prioritizedContentTypes;
    const toneRaw = origStyleInsights.tonePreference || origStyleInsights.editingTonePreference;

    const styleInsights = {
      contentPriorities: ensureStringArray(contentPrioritiesRaw),
      tonePreference: toneRaw || null,
      pacingStyle: origStyleInsights.pacingStyle || null,
    };

    const referenceExamples = {
      keptSegments: state.keptExamples.filter(e => e.enabled).map(e => e.text),
      removedSegments: state.removedExamples.filter(e => e.enabled).map(e => e.text),
    };

    const origDeletionCount = (input.deletionPatterns || []).length;
    const origAdditionCount = (input.additionPatterns || []).length;
    const origAgg = normalizeAggressiveness(
      origStructural.aggressivenessLevel || origStructural.overallAggressiveness || 'moderate'
    );

    const disabledDeletions = state.deletionPatterns.filter(p => !p.enabled).length;
    const disabledAdditions = state.additionPatterns.filter(a => !a.enabled).length;
    const deletionsWithNotes = state.deletionPatterns.filter(p => p.note && p.note.trim()).length;
    const additionsModified = state.additionPatterns.filter(a =>
      a.userDesc || a.note || a.classification !== a.type || a.position !== normalizePosition(
        (input.additionPatterns || []).find(orig => orig.fileInfo?.title === a.fileTitle)?.positionType,
        (input.additionPatterns || []).find(orig => orig.fileInfo?.title === a.fileTitle)?.placement
      )
    ).length;
    const aggChanged = state.aggressiveness !== origAgg;

    const origKeptCount = ensureStringArray(
      (input.referenceExamples || {}).keptSegments || (input.referenceExamples || {}).keptSnippets
    ).length;
    const origRemovedCount = ensureStringArray(
      (input.referenceExamples || {}).removedSegments || (input.referenceExamples || {}).removedSnippets
    ).length;
    const examplesModified =
      referenceExamples.keptSegments.length !== origKeptCount ||
      referenceExamples.removedSegments.length !== origRemovedCount;

    const userModifiedPatterns =
      disabledDeletions > 0 || disabledAdditions > 0 || deletionsWithNotes > 0 ||
      additionsModified > 0 || aggChanged || examplesModified;

    const output = {
      referenceEditingPatterns: {
        deletionPatterns,
        structuralPatterns,
        additionPatterns,
        styleInsights,
        referenceExamples,
      },
      originalPatternsS3Url: state.patternsS3Url || null,
      userModifiedPatterns,
      modificationSummary: {
        deletionPatternsDisabled: disabledDeletions,
        deletionPatternsWithNotes: deletionsWithNotes,
        additionPatternsDisabled: disabledAdditions,
        additionPatternsModified: additionsModified,
        aggressivenessChanged: aggChanged,
        referenceExamplesModified: examplesModified,
      },
    };

    onApprove(output);
  }, [state, onApprove]);

  // ─── Derived counts ─────────────────────────────────────────────────────

  const activeDeletions = state.deletionPatterns.filter(p => p.enabled).length;
  const totalDeletions = state.deletionPatterns.length;
  const activeAdditions = state.additionPatterns.filter(a => a.enabled).length;
  const totalAdditions = state.additionPatterns.length;
  const totalActive = activeDeletions + activeAdditions;
  const allDisabled = totalActive === 0 && (totalDeletions > 0 || totalAdditions > 0);

  const estimatedReduction = useMemo(() => {
    if (state.deletionPercentage) {
      const base = state.deletionPercentage;
      const map = { light: Math.round(base * 0.5), moderate: Math.round(base), aggressive: Math.round(base * 1.7) };
      return map[state.aggressiveness] || map.moderate;
    }
    const fallback = { light: 12, moderate: 25, aggressive: 42 };
    return fallback[state.aggressiveness] || 25;
  }, [state.aggressiveness, state.deletionPercentage]);

  // ─── Imperative handle ─────────────────────────────────────────────────

  useImperativeHandle(ref, () => ({
    approve: handleApprove,
    reset: handleReset,
    getActiveCount: () => totalActive,
  }), [handleApprove, handleReset, totalActive]);

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <>
    <style>{getScopedStyles(isDarkMode)}</style>
    <div className="w-full pattern-review-themed" style={{ ...themeVars, fontSize: 'var(--text-base, 16px)', color: 'var(--text-default-color)', padding: '12px 0' }}>
      {/* ELM v2: Clean mapping banner */}
      {showCleanMappingBanner && (
        <div className="mb-3 px-3 py-2"
          style={{
            background: 'var(--trebble-success-bg, rgba(58, 158, 74, 0.08))',
            borderLeft: '3px solid var(--trebble-success, #3a9e4a)',
            color: 'var(--text-default-color)',
            fontSize: 'var(--text-base, 16px)',
          }}>
          {t('STYLE_MAPS_CLEANLY')}
        </div>
      )}
      {/* What we'll cut */}
      <div className="mb-4">
        <h3 className="font-semibold mb-2 flex items-baseline gap-2"
          style={{ color: 'var(--text-default-color)', fontSize: 'var(--text-base, 16px)' }}>
          {t('WHAT_TO_REMOVE')}
          <span className="font-normal" style={{ color: 'var(--text-secondary-color)', fontSize: 'var(--text-xs, 12px)' }}>
            {totalDeletions > 0
              ? t('DELETION_STATS_TEMPLATE').replace('{active}', activeDeletions).replace('{total}', totalDeletions).replace('{pct}', estimatedReduction)
              : t('NONE_DETECTED')}
          </span>
        </h3>

        {totalDeletions === 0 && (
          <div className="p-3 italic border"
            style={{
              fontSize: 'var(--text-base, 16px)',
              color: 'var(--text-tertiary-color)',
              borderColor: 'var(--border-default-color)',
              background: 'var(--bg-card)',
            }}>
            {t('NO_PATTERNS_DETECTED')}
          </div>
        )}

        {state.deletionPatterns.map(pattern => (
          <DeletionPatternItem
            key={pattern._id}
            pattern={pattern}
            isExpanded={expandedItems.has(pattern._id)}
            onToggleExpand={toggleExpand}
            onToggleEnabled={toggleDeletionEnabled}
            onNoteChange={setDeletionNote}
            removedExamples={state.removedExamples}
            keptExamples={state.keptExamples}
          />
        ))}
      </div>

      {/* What we'll add */}
      <div className="mb-4">
        <h3 className="font-semibold mb-2 flex items-baseline gap-2"
          style={{ color: 'var(--text-default-color)', fontSize: 'var(--text-base, 16px)' }}>
          {t('WHAT_TO_ADD')}
          <span className="font-normal" style={{ color: 'var(--text-secondary-color)', fontSize: 'var(--text-xs, 12px)' }}>
            {totalAdditions > 0
              ? `${activeAdditions}/${totalAdditions}`
              : t('NONE_DETECTED')}
          </span>
        </h3>

        {totalAdditions === 0 && (
          <div className="p-3 italic border"
            style={{
              fontSize: 'var(--text-base, 16px)',
              color: 'var(--text-tertiary-color)',
              borderColor: 'var(--border-default-color)',
              background: 'var(--bg-card)',
            }}>
            {t('NO_ADDITIONS_DETECTED')}
          </div>
        )}

        {state.additionPatterns.map(addition => (
          <AdditionPatternItem
            key={addition._id}
            addition={addition}
            isExpanded={expandedItems.has(addition._id)}
            onToggleExpand={toggleExpand}
            onToggleEnabled={toggleAdditionEnabled}
            onNoteChange={setAdditionNote}
            onPositionChange={setPosition}
            onCustomPositionChange={setCustomPosition}
          />
        ))}
      </div>

      {/* Warning when all disabled */}
      {allDisabled && (
        <div className="p-2 text-center mb-2 border"
          style={{
            color: 'var(--trebble-warning)',
            borderColor: 'var(--trebble-warning)',
            background: 'var(--trebble-warning-bg)',
          }}>
          {t('ALL_PATTERNS_DISABLED_WARNING')}
        </div>
      )}
    </div>
    </>
  );
});

export default PatternReviewStep;
